import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MhTopicsAPI from '../../api/mhTopics';

const BADGE_DEBOUNCE_MS = 2000;
const BADGE_REFRESH_MS = 60000;

let badgeDebounceTimer;
let badgeRefreshInterval;
let refreshHandlers;

export const state = {
  records: [],
  badges: {},
  accountId: null,
  latestStructureRequestId: 0,
  latestBadgeRequestId: 0,
  uiFlags: {
    isFetching: false,
    isFetchingBadges: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isReordering: false,
  },
};

export const getters = {
  getTopics: $state =>
    [...$state.records].sort(
      (first, second) =>
        first.position - second.position || first.id - second.id
    ),
  getTopicById: $state => id =>
    $state.records.find(topic => topic.id === Number(id)),
  getBadgeFor: $state => id => Number($state.badges[id]) || 0,
  getUIFlags: $state => $state.uiFlags,
};

const currentAccountId = rootGetters =>
  Number(rootGetters.getCurrentAccountId) || null;

const scheduleBadgeRefresh = dispatch => {
  clearTimeout(badgeDebounceTimer);
  badgeDebounceTimer = setTimeout(
    () => dispatch('fetchBadges'),
    BADGE_DEBOUNCE_MS
  );
};

export const actions = {
  async get({ commit, state: $state, rootGetters }) {
    const accountId = currentAccountId(rootGetters);
    if (!accountId) return;

    commit('PREPARE_ACCOUNT', accountId);
    const requestId = $state.latestStructureRequestId + 1;
    commit('STRUCTURE_REQUEST_STARTED', requestId);
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await MhTopicsAPI.get();
      if ($state.accountId !== accountId) return;
      if ($state.latestStructureRequestId !== requestId) return;
      commit('SET_TOPICS', data.payload);
    } catch {
      // Keep the last good structure; cable, account switch or mount retries.
    } finally {
      if (
        $state.accountId === accountId &&
        $state.latestStructureRequestId === requestId
      ) {
        commit('SET_UI_FLAG', { isFetching: false });
      }
    }
  },

  async fetchBadges({ commit, state: $state, rootGetters }) {
    const accountId = currentAccountId(rootGetters);
    if (!accountId) return;

    commit('PREPARE_ACCOUNT', accountId);
    const requestId = $state.latestBadgeRequestId + 1;
    commit('BADGE_REQUEST_STARTED', requestId);
    commit('SET_UI_FLAG', { isFetchingBadges: true });
    try {
      const { data } = await MhTopicsAPI.badges();
      if ($state.accountId !== accountId) return;
      if ($state.latestBadgeRequestId !== requestId) return;
      commit('SET_BADGES', data);
    } catch {
      // Keep the last good counts; event, visibility and interval refreshes retry.
    } finally {
      if (
        $state.accountId === accountId &&
        $state.latestBadgeRequestId === requestId
      ) {
        commit('SET_UI_FLAG', { isFetchingBadges: false });
      }
    }
  },

  async create({ commit, dispatch, state: $state, rootGetters }, data) {
    const accountId = currentAccountId(rootGetters);
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const response = await MhTopicsAPI.create(data);
      if ($state.accountId !== accountId) return response.data;
      commit('ADD_TOPIC', response.data);
      await dispatch('fetchBadges');
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update({ commit, dispatch, state: $state, rootGetters }, data) {
    const accountId = currentAccountId(rootGetters);
    const { id, ...attributes } = data;
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await MhTopicsAPI.update(id, attributes);
      if ($state.accountId !== accountId) return response.data;
      commit('UPDATE_TOPIC', response.data);
      await dispatch('fetchBadges');
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async delete({ commit, dispatch, state: $state, rootGetters }, topic) {
    const accountId = currentAccountId(rootGetters);
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await MhTopicsAPI.delete(topic.id, { lock_version: topic.lock_version });
      if ($state.accountId !== accountId) return;
      commit('DELETE_TOPIC', topic.id);
      await dispatch('fetchBadges');
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async reorder(
    { commit, dispatch, getters: moduleGetters, state: $state, rootGetters },
    orderedIds
  ) {
    const accountId = currentAccountId(rootGetters);
    const expectedOrderIds = moduleGetters.getTopics.map(topic => topic.id);
    commit('SET_UI_FLAG', { isReordering: true });
    try {
      const response = await MhTopicsAPI.reorder({
        expected_order_ids: expectedOrderIds,
        ordered_ids: orderedIds,
      });
      if ($state.accountId !== accountId) return response.data.payload;
      commit('SET_TOPICS', response.data.payload);
      await dispatch('fetchBadges');
      return response.data.payload;
    } catch (error) {
      const currentTopics = error?.response?.data?.payload;
      if (
        $state.accountId === accountId &&
        error?.response?.status === 409 &&
        currentTopics
      ) {
        commit('SET_TOPICS', currentTopics);
      }
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isReordering: false });
    }
  },

  async handleStructureUpdate({ dispatch }) {
    await dispatch('get');
    await dispatch('fetchBadges');
  },

  startBadgeRefresh({ dispatch }) {
    if (refreshHandlers) return;

    const onReplyStateChanged = () => scheduleBadgeRefresh(dispatch);
    const onReconnect = () => dispatch('fetchBadges');
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') dispatch('fetchBadges');
    };
    emitter.on('fetch_needs_reply_count', onReplyStateChanged);
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, onReconnect);
    document.addEventListener('visibilitychange', onVisibilityChange);
    badgeRefreshInterval = setInterval(() => {
      if (document.visibilityState === 'visible') dispatch('fetchBadges');
    }, BADGE_REFRESH_MS);
    refreshHandlers = { onReplyStateChanged, onReconnect, onVisibilityChange };
  },

  stopBadgeRefresh() {
    if (!refreshHandlers) return;

    emitter.off('fetch_needs_reply_count', refreshHandlers.onReplyStateChanged);
    emitter.off(
      BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
      refreshHandlers.onReconnect
    );
    document.removeEventListener(
      'visibilitychange',
      refreshHandlers.onVisibilityChange
    );
    clearTimeout(badgeDebounceTimer);
    clearInterval(badgeRefreshInterval);
    refreshHandlers = null;
  },
};

export const mutations = {
  PREPARE_ACCOUNT($state, accountId) {
    if ($state.accountId === accountId) return;
    $state.accountId = accountId;
    $state.records = [];
    $state.badges = {};
    $state.latestStructureRequestId += 1;
    $state.latestBadgeRequestId += 1;
  },
  STRUCTURE_REQUEST_STARTED($state, requestId) {
    $state.latestStructureRequestId = requestId;
  },
  BADGE_REQUEST_STARTED($state, requestId) {
    $state.latestBadgeRequestId = requestId;
  },
  SET_TOPICS($state, topics) {
    $state.records = topics;
  },
  ADD_TOPIC($state, topic) {
    $state.records.push(topic);
  },
  UPDATE_TOPIC($state, topic) {
    const index = $state.records.findIndex(record => record.id === topic.id);
    if (index >= 0) $state.records.splice(index, 1, topic);
  },
  DELETE_TOPIC($state, id) {
    $state.records = $state.records.filter(topic => topic.id !== id);
  },
  SET_BADGES($state, badges) {
    $state.badges = badges;
  },
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
